import { Component, computed, inject, signal } from '@angular/core';
import { ScreenNavigationService } from '../../core/services/screen-navigation';
import { DailyChallengeService } from '../../core/services/daily-challenge';
import { DailyChallengeSessionStatus } from '../../core/models/cache/daily-challenge-cache-entry';
import { apiErrorDetail } from '../../core/models/responses/api-error-response';
import { DatePicker, DayClassFn } from '../../shared/components/date-picker/date-picker';
import { BackButton } from '../../shared/components/back-button/back-button';
import { PageLoader } from '../../shared/components/page-loader/page-loader';
import { AlertModal } from '../../shared/components/alert-modal/alert-modal';
import { DAILY_CHALLENGE_START_DATE } from '../../shared/constants/daily-challenge';
import { IsoDate, todayInBrasilia } from '../../shared/utils/iso-date';

/** Classe do dia no calendário conforme o status salvo (classes globais em styles.scss). */
const DAY_CLASS_BY_STATUS: Record<DailyChallengeSessionStatus, string> = {
  VITORIA: 'calendar-day--won',
  DERROTA: 'calendar-day--lost',
  EM_ANDAMENTO: 'calendar-day--in-progress',
};

const ALREADY_PLAYED_MESSAGE = 'Você já jogou o desafio deste dia.';
const START_ERROR_MESSAGE = 'Não foi possível iniciar o desafio. Tente novamente.';

/** Escolha do dia do Desafio Diário — 2º passo, após o tema (daily-theme). */
@Component({
  selector: 'app-daily-calendar',
  imports: [DatePicker, BackButton, PageLoader, AlertModal],
  templateUrl: './daily-calendar.html',
  styleUrl: './daily-calendar.scss',
})
export class DailyCalendarPage {
  private readonly screenNavigation = inject(ScreenNavigationService);
  private readonly dailyChallenge = inject(DailyChallengeService);

  protected readonly theme = this.dailyChallenge.theme;
  protected readonly selectedDate = this.dailyChallenge.date;
  protected readonly minDate = DAILY_CHALLENGE_START_DATE;
  /** Último dia selecionável: hoje, no horário de Brasília. */
  protected readonly maxDate = todayInBrasilia();

  /** Dias já jogados (ou em andamento) no tema, vindos do cache local. */
  private readonly playedDays = signal(this.dailyChallenge.playedDays());
  protected readonly dayClass = computed<DayClassFn>(() => {
    const playedDays = this.playedDays();
    return (date) => {
      const status = playedDays.get(date);
      return status ? DAY_CLASS_BY_STATUS[status] : null;
    };
  });

  protected readonly isStarting = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly showAlreadyPlayedAlert = signal(false);
  protected readonly alreadyPlayedMessage = ALREADY_PLAYED_MESSAGE;

  ngOnInit(): void {
    // O calendário depende do tema; sem ele (estado perdido), volta para a escolha de tema.
    if (!this.theme()) {
      this.screenNavigation.goToDailyTheme();
    }
  }

  /** Escolher o dia já inicia (ou retoma) o desafio — não há botão "Jogar". */
  protected onDaySelect(date: IsoDate): void {
    if (this.isStarting()) {
      return;
    }

    this.errorMessage.set(null);
    this.dailyChallenge.selectDate(date);

    const status = this.playedDays().get(date);
    if (status === 'VITORIA' || status === 'DERROTA') {
      this.showAlreadyPlayedAlert.set(true);
      return;
    }

    this.isStarting.set(true);
    // Em caso de sucesso o loading permanece até a troca de tela (como no Modo Solo).
    this.dailyChallenge
      .play()
      .then((outcome) => {
        if (outcome === 'already-played') {
          this.isStarting.set(false);
          this.showAlreadyPlayedAlert.set(true);
          this.playedDays.set(this.dailyChallenge.playedDays());
        }
      })
      .catch((error: unknown) => {
        console.error('Não foi possível iniciar o Desafio Diário.', error);
        this.isStarting.set(false);
        this.errorMessage.set(apiErrorDetail(error) ?? START_ERROR_MESSAGE);
        this.playedDays.set(this.dailyChallenge.playedDays());
      });
  }

  protected onBackClick(): void {
    this.screenNavigation.goToDailyTheme();
  }
}
